/**
 * The dot derivation both log adapters share (docs/spec/21i-spraywall-logging.md,
 * "Adapter output").
 *
 * It lives in its own module because two adapters feed it: `routeLogModel.js`
 * (the route modal) and `sprayLogModel.js` (the Spraywall problem view). When it
 * sat inside the route adapter, the spray adapter imported a route file for a
 * function that knows nothing about routes. Both adapters normalize their
 * entries into the same `items` shape and call `buildLogModel`, so the route and
 * the spray problem cannot drift apart.
 *
 * Pure and client-only: no i18n, no Firestore, no clock (`today` is a parameter).
 */

export const isCount = (v) => typeof v === "number" && Number.isFinite(v);

// A try entry's `attempts` as a number of dots; anything unusable draws none.
const dotCount = (v) => (isCount(v) && v > 0 ? Math.trunc(v) : 0);

/**
 * The shared dot derivation of both adapters (docs/spec/21i-spraywall-logging.md,
 * "Adapter output"). No dot is stored: this turns the daily-log entries and the
 * ascent into the days the log area draws.
 *
 * `items` are the adapter-normalized entries of ONE route / problem:
 * `{ date, kind: "try" | "send" | "repeat", entryId, at, attempts }`. A `try`
 * item is the day's single try entry (its `attempts` is the number of real try
 * dots); `send` and `repeat` items give one dot each.
 *
 * The send's day takes its tries from the ASCENT, not from the day's entry:
 * "7 tries, then a send" is stored as 7 + an 8-try ascent, and must read as
 * 7 dots and a ✓ — so the dots up to the send are `ascent.attempts − 1`, the
 * ones the log does not hold one by one are virtual.
 */
export function buildLogModel({ state, ascent = null, items = [], today = null, archived = false } = {}) {
  const sent = state === "sent";
  const flash = sent && ascent?.flash === true;
  const countKnown = sent && !flash && isCount(ascent?.attempts);
  const sendCount = !sent ? null : flash ? 1 : isCount(ascent?.attempts) ? ascent.attempts : null;

  const byDate = new Map();
  const dayOf = (date) => {
    if (!byDate.has(date)) byDate.set(date, { date, tryItem: null, repeats: [] });
    return byDate.get(date);
  };

  let sendItem = null;
  let tryEntryCount = 0;
  for (const item of Array.isArray(items) ? items : []) {
    if (!item || !item.date) continue;
    const day = dayOf(item.date);
    if (item.kind === "try") {
      if (!day.tryItem) {
        day.tryItem = item;
        tryEntryCount += 1;
      }
    } else if (item.kind === "send") {
      // Only an ascent makes a send; the earliest entry stands for it.
      if (sent && (!sendItem || item.date < sendItem.date || (item.date === sendItem.date && item.at < sendItem.at))) {
        sendItem = item;
      }
    } else if (item.kind === "repeat") {
      day.repeats.push(item);
    }
  }

  // The ascent can be loaded before its log entry (the route modal does not
  // wait for the fetch in the sent state), so its own date places the ✓ too.
  const sendDay = sent ? (sendItem?.date ?? ascent?.date ?? null) : null;
  if (sendDay) dayOf(sendDay);
  if (!archived && today) dayOf(today);

  const dates = [...byDate.keys()].sort();
  const realOf = (day) => (day.tryItem ? dotCount(day.tryItem.attempts) : 0);
  let earlierTries = 0;
  if (sendDay) for (const d of dates) if (d < sendDay) earlierTries += realOf(byDate.get(d));

  const days = [];
  let tries = 0;
  let triesBeforeSend = 0;
  let loggedTriesBeforeSend = 0;
  let repeatCount = 0;
  let projectDays = 0;
  let realTotal = 0;

  for (const date of dates) {
    const day = byDate.get(date);
    const real = realOf(day);
    const isSendDay = sendDay === date;
    const virtual = isSendDay && countKnown
      ? Math.max(0, Math.trunc(ascent.attempts) - 1 - earlierTries - real)
      : 0;

    const dots = [];
    for (let i = 0; i < real + virtual; i += 1) {
      const isVirtual = i >= real;
      dots.push({
        key: `${date}/try/${i}`,
        kind: "try",
        entryId: isVirtual ? null : day.tryItem.entryId,
        at: 0,
        virtual: isVirtual,
      });
    }
    if (isSendDay) {
      if (!flash && !countKnown) {
        dots.push({ key: `${date}/unknown`, kind: "unknown", entryId: null, at: 0, virtual: false });
      }
      dots.push({
        key: `${date}/send`,
        kind: flash ? "flash" : "send",
        entryId: sendItem?.entryId ?? null,
        at: sendItem?.at ?? 0,
        virtual: false,
      });
    }
    const repeats = [...day.repeats].sort((a, b) => a.at - b.at || String(a.entryId).localeCompare(String(b.entryId)));
    for (const r of repeats) {
      dots.push({ key: `${date}/${r.entryId}`, kind: "repeat", entryId: r.entryId, at: r.at, virtual: false });
    }

    tries += real + virtual;
    realTotal += real;
    repeatCount += repeats.length;
    if (real > 0) projectDays += 1;
    if (sendDay && date <= sendDay) {
      triesBeforeSend += real + virtual;
      loggedTriesBeforeSend += real;
    }

    const isToday = date === today;
    // A day without a dot exists only as the (empty) today.
    if (dots.length > 0 || (isToday && !archived)) days.push({ date, today: isToday, dots });
  }

  const unknownCount = sent && !flash && !isCount(ascent?.attempts);
  let totalAttempts = null;
  if (sent) totalAttempts = isCount(ascent?.attempts) ? ascent.attempts : null;
  else if (state === "project") totalAttempts = isCount(ascent?.attempts) ? ascent.attempts : realTotal;

  const sendDate = sent ? (ascent?.date ?? sendDay) : null;

  return {
    state,
    flash,
    sendCount,
    days,
    summary: {
      sendDate,
      tries,
      triesBeforeSend,
      earlierTries,
      loggedTriesBeforeSend,
      repeatCount,
      projectDays,
      unknownCount,
      totalAttempts,
    },
    // D13: turning a plain send into a flash deletes it and writes a new one,
    // which takes nothing else with it only when the send stands alone today.
    rewriteFlashAllowed: sent && !flash && tryEntryCount === 0 && repeatCount === 0 && !!today && sendDate === today,
  };
}
